import { useEffect, useState } from 'react';
import { api, type ComedyDashboard } from '../../lib/api';
import { Button, Card, ErrorAlert, Spinner } from '../../components/ui';
import { ComedySection, ComedyStat, formatDate, formatUsd } from './ComedyShared';

export function ComedyDashboardPanel({ onOpenChannel, onOpenTab }: { onOpenChannel: (channelId: string) => void; onOpenTab: (tab: string) => void }) {
  const [data, setData] = useState<ComedyDashboard | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.comedy.dashboard()
      .then((value) => { if (alive) setData(value); })
      .catch((err) => { if (alive) setError(err); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tick]);

  if (loading && !data) return <Spinner label="Ładowanie Comedy Studio…" />;
  if (error) return <ErrorAlert error={error} />;
  if (!data) return null;

  return (
    <div className="stack" style={{ display: 'grid', gap: 16 }}>
      <div className="grid grid--stats">
        <ComedyStat label="Kanały" value={data.channels.total} hint={`${data.channels.active} aktywnych · ${data.channels.autopilot} AUTOPILOT`} />
        <ComedyStat label="Profile social" value={data.accounts.active} hint={`${data.accounts.total} przypisanych`} />
        <ComedyStat label="Materiały" value={data.research.total} hint={`${data.research.cleared} z potwierdzonymi prawami`} />
        <ComedyStat label="Wypowiedzi" value={data.segments} hint="aktywne segmenty w bibliotece" />
        <ComedyStat label="Scenariusze" value={data.scenarios.draft + data.scenarios.review} hint={`${data.scenarios.review} do recenzji · ${data.scenarios.approved} zatwierdzonych`} tone={data.scenarios.review ? 'warning' : undefined} />
        <ComedyStat label="Produkcje" value={data.productions.queued + data.productions.rendering + data.productions.qa + data.productions.ready} hint={`${data.productions.rendering} renderuje · ${data.productions.ready} gotowych`} />
        <ComedyStat label="Publikacje" value={data.publications.published} hint={`${data.publications.scheduled} zaplanowanych`} />
        <ComedyStat label="Błędy 24h" value={data.errorsLast24h} tone={data.errorsLast24h ? 'danger' : undefined} />
        <ComedyStat label="Koszt AI dziś" value={formatUsd(data.aiCosts.todayUsd)} hint={`miesiąc: ${formatUsd(data.aiCosts.monthUsd)}`} />
      </div>

      <div className="grid grid--2">
        <ComedySection
          title="Kanały tematyczne"
          description="Każdy kanał ma własne źródła, bibliotekę, scenariusze i profile social media."
          actions={<Button size="sm" onClick={() => onOpenTab('channels')}>Zarządzaj</Button>}
        >
          {data.channelStats.length === 0 ? (
            <div className="muted">Nie masz jeszcze kanału. Utwórz pierwszy kanał w zakładce „Kanały tematyczne”.</div>
          ) : (
            <div className="table-wrap" style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead><tr><th>Kanał</th><th>Tryb</th><th>Materiały</th><th>Wypowiedzi</th><th>Scenariusze</th><th>Produkcje</th><th>Publikacje</th><th>AI dziś</th><th /></tr></thead>
                <tbody>
                  {data.channelStats.map((channel) => (
                    <tr key={channel.channelId}>
                      <td><strong>{channel.name}</strong><div className="muted" style={{ fontSize: 11 }}>{channel.language.toUpperCase()} · {channel.status}</div></td>
                      <td>{channel.automationMode}</td>
                      <td>{channel.researchCount}</td>
                      <td>{channel.segmentCount}</td>
                      <td>{channel.pendingScenarios}</td>
                      <td>{channel.activeProductions}</td>
                      <td>{channel.published}</td>
                      <td>{formatUsd(channel.costTodayUsd)}</td>
                      <td><Button size="sm" onClick={() => onOpenChannel(channel.channelId)}>Otwórz</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ComedySection>

        <ComedySection title="VPS render farm" description="Stan z ostatniego heartbeatu renderera." actions={<Button size="sm" onClick={() => setTick((value) => value + 1)}>Odśwież</Button>}>
          {data.renderNodes.length === 0 ? (
            <div className="muted">Brak zgłoszonego renderera. Sprawdź usługę `kontentowo-video-renderer` na VPS.</div>
          ) : (
            <div className="table-wrap" style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead><tr><th>Node</th><th>Status</th><th>Zadania</th><th>RAM wolny</th><th>Load</th><th>Heartbeat</th></tr></thead>
                <tbody>
                  {data.renderNodes.map((node) => (
                    <tr key={node.id}>
                      <td><strong>{node.id}</strong><div className="muted" style={{ fontSize: 11 }}>{node.name ?? ''}</div></td>
                      <td>{node.status}</td>
                      <td>{node.activeJobs}/{node.maxConcurrency}{node.availableSlots != null ? ` · sloty ${node.availableSlots}` : ''}</td>
                      <td>{node.memoryFreeBytes != null ? `${(node.memoryFreeBytes / 1024 ** 3).toFixed(1)} GB` : '—'}</td>
                      <td>{node.load != null ? node.load.toFixed(2) : '—'}</td>
                      <td>{formatDate(node.lastHeartbeat)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ComedySection>
      </div>

      <Card title="Pierwsze kroki">
        <ol style={{ margin: 0, paddingLeft: 20, lineHeight: 1.9 }}>
          <li>Utwórz kanał tematyczny i ustaw język, tematykę oraz harmonogram.</li>
          <li>Przypisz istniejące profile YouTube / Facebook / Instagram / TikTok — integracja pokaże realny status autoryzacji.</li>
          <li>Uruchom research AI i zaimportuj wyłącznie materiały z potwierdzonymi prawami.</li>
          <li>Uruchom transkrypcję (lokalny whisper.cpp na VPS), a następnie wyszukaj wypowiedzi semantycznie.</li>
          <li>Wygeneruj scenariusze (domyślnie HYBRID AUTO), zatwierdź najlepszy i uruchom render.</li>
          <li>Po QA zatwierdź film i zaplanuj publikację na wybranych profilach.</li>
        </ol>
      </Card>
    </div>
  );
}
